export type TModel = {
  //Array of world coordinates in meters
  //The origin (0,0) is the bottom right of the dugouts when seen from behind the dugout,
  //or in case of a single camera, it is always bottom right
  worldCoords: Array<[number, number, number]>;
  //Array of list of world coordinates which form specific shapes that must be drawn
  shapes: Array<Array<number>>;
  //Array of tuples which form lines that must be drawn
  lines: Array<Array<number>>;
  //The outer corners of the pitch model
  corners: Array<number>;
  //The displayed name of the model
  name: string;
};

const GOAL_WIDTH = 7.32;
const PENALTY_BOX_HEIGHT = 16.5;
const GOAL_BOX_HEIGHT = 5.5;
const GOAL_BOX_TO_POST = 5.5;
const CIRCLE_WIDTH = 2 * 9.15;
const PENALTY_BOX_TO_GOAL_BOX = 10.97;
const GOAL_BOX_WIDTH = 2 * GOAL_BOX_TO_POST + GOAL_WIDTH;
const PENALTY_BOX_WIDTH =
  2 * GOAL_BOX_TO_POST + 2 * PENALTY_BOX_TO_GOAL_BOX + GOAL_WIDTH;
const HALF_CIRCLE_WIDTH = 14.63;
const GOAL_POST_HEIGHT = 2.44;
//Bit atypical but coords are in y,x because of easier draw

export const FutsalModelExtended = (w: number, h: number): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, w / 2 - 7.5, 0], //1
      [0, w / 2 - 1.5, 0], //2
      [0, w / 2 + 1.5, 0], //3
      [0, w / 2 + 7.5, 0], //4
      [0, w, 0], //5
      [h / 2, w, 0], //6
      [h, w, 0], //7
      [h, w / 2 + 7.5, 0], //8
      [h, w / 2 + 1.5, 0], //9
      [h, w / 2 - 1.5, 0], //10
      [h, w / 2 - 7.5, 0], //11
      [h, 0, 0], //12
      [h / 2 + 10, 0, 0], //13
      [h / 2 + 5, 0, 0], //14
      [h / 2, 0, 0], //15
      [h / 2 - 5, 0, 0], //16
      [h / 2 - 10, 0, 0], //17
      [h / 2, w / 2 - 3, 0], //18 kickoff circle
      [h / 2, w / 2, 0], //19 kickoff circle middle
      [h / 2, w / 2 + 3, 0], //20 kickoff circle
      [6, w / 2, 0], //21 first penalty mark #1
      [10, w / 2, 0], //22 second penalty mark #1
      [h - 6, w / 2, 0], //23 first penalty mark #2
      [h - 10, w / 2, 0], //24 second penalty mark #2
    ],
    shapes: [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 0]],
    lines: [
      [15, 18],
      [18, 19],
      [19, 20],
      [20, 6],
      [21, 22],
      [21, 2],
      [21, 3],
      [23, 24],
      [23, 9],
      [23, 10],
    ],
    corners: [0, 7],
    name: "Futsal extended (" + w + "x" + h + ")",
  };
};

export const FutsalModel = (w: number, h: number): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, w, 0], //1
      [h / 2, w, 0], //2
      [h, w, 0], //3
      [h, 0, 0], //4
      [h / 2, 0, 0], //5
    ],
    shapes: [[0, 1, 2, 3, 4, 5, 0]],
    lines: [[2, 5]],
    corners: [0, 3],
    name: "Futsal (20x40)",
  };
};

export const FutsalModel15 = (w: number, l: number, goalWidth = 5): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, w / 2 - goalWidth / 2, 0], //1
      [0, w / 2 + goalWidth / 2, 0], //2
      [0, w, 0], //3
      [8, w, 0], //4
      [l / 2, w, 0], //5
      [l - 8, w, 0], //6
      [l, w, 0], //7
      [l, w / 2 + goalWidth / 2, 0], //8
      [l, w / 2 - goalWidth / 2, 0], //9
      [l, 0, 0], //10
      [l - 8, 0, 0], //11
      [l / 2, 0, 0], //12
      [8, 0, 0], //13
      [l / 2, w / 2, 0], //14
    ],
    shapes: [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 0]],
    lines: [
      [5, 14],
      [14, 12],
    ],
    corners: [0, 7],
    name: "Futsal 15 points (20x40)",
  };
};

export const FutsalModelTopOfBox = (w: number, h: number): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, w, 0], //1
      [h / 2, w, 0], //2
      [h, w, 0], //3
      [h, 0, 0], //4
      [h / 2 + HALF_CIRCLE_WIDTH / 2, 0, 0], //5
      [h / 2, 0, 0], //6
      [h / 2 - HALF_CIRCLE_WIDTH / 2, 0, 0], //7
    ],
    shapes: [[0, 1, 2, 3, 4, 5, 6, 7, 0]],
    lines: [[2, 6]],
    corners: [0, 3],
    name: "Futsal on top of box (20x40)",
  };
};

const cornerToGoalPost = (w: number) => (w - GOAL_WIDTH) / 2;
const cornerToGoalBox = (w: number) => (w - GOAL_BOX_WIDTH) / 2;
const cornerToPenaltyBox = (w: number) => (w - PENALTY_BOX_WIDTH) / 2;

export const FullPitchModel = (w: number, h: number): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, w, 0], //1
      [h / 2, w, 0], //2
      [h, w, 0], //3
      [h, 0, 0], //4
      [h / 2, 0, 0], //5
      [0, cornerToPenaltyBox(w), 0], //16m bottom
      [PENALTY_BOX_HEIGHT, cornerToPenaltyBox(w), 0], //7
      [PENALTY_BOX_HEIGHT, w - cornerToPenaltyBox(w), 0], //8
      [0, w - cornerToPenaltyBox(w), 0], //9
      [h, cornerToPenaltyBox(w), 0], //16m top
      [h - PENALTY_BOX_HEIGHT, cornerToPenaltyBox(w), 0], //11
      [h - PENALTY_BOX_HEIGHT, w - cornerToPenaltyBox(w), 0], //12
      [h, w - cornerToPenaltyBox(w), 0], //13
      [0, cornerToGoalBox(w), 0], //14
      [GOAL_BOX_HEIGHT, cornerToGoalBox(w), 0], //15
      [GOAL_BOX_HEIGHT, w - cornerToGoalBox(w), 0], //16
      [0, w - cornerToGoalBox(w), 0], //17
      [h, cornerToGoalBox(w), 0], //18
      [h - GOAL_BOX_HEIGHT, cornerToGoalBox(w), 0], //19
      [h - GOAL_BOX_HEIGHT, w - cornerToGoalBox(w), 0], //20
      [h, w - cornerToGoalBox(w), 0], //21
      [h / 2, w / 2, 0], //Middle
      [0, cornerToGoalPost(w), 0], //23
      [0, w - cornerToGoalPost(w), 0], //24
      [PENALTY_BOX_HEIGHT, (w - HALF_CIRCLE_WIDTH) / 2, 0], //25
      [PENALTY_BOX_HEIGHT, w - (w - HALF_CIRCLE_WIDTH) / 2, 0], //26
      [h, cornerToGoalPost(w), 0], //27
      [h, w - cornerToGoalPost(w), 0], //28
      [h - PENALTY_BOX_HEIGHT, w - (w - HALF_CIRCLE_WIDTH) / 2, 0], //29
      [h - PENALTY_BOX_HEIGHT, (w - HALF_CIRCLE_WIDTH) / 2, 0], //30
      [h / 2, w - (w - CIRCLE_WIDTH) / 2, 0], //31
      [h / 2, (w - CIRCLE_WIDTH) / 2, 0], //32
    ],
    shapes: [
      [0, 6, 14, 23, 24, 17, 9, 1, 2, 3, 13, 21, 28, 27, 18, 10, 4, 5, 0],
    ],
    lines: [
      [2, 31],
      [31, 22],
      [22, 32],
      [32, 5],
      [6, 7],
      [7, 25],
      [25, 26],
      [26, 8],
      [8, 9],
      [10, 11],
      [11, 30],
      [30, 29],
      [29, 12],
      [12, 13],
      [14, 15],
      [15, 16],
      [16, 17],
      [18, 19],
      [19, 20],
      [20, 21],
    ],
    corners: [0, 3],
    name: "Full pitch (" + w + "x" + h + ")",
  };
};

export const FullPitchExtendedModel = (w: number, h: number): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, w, 0], //1
      [h / 2, w, 0], //2
      [h, w, 0], //3
      [h, 0, 0], //4
      [h / 2, 0, 0], //5
      [0, cornerToPenaltyBox(w), 0], //16m bottom
      [PENALTY_BOX_HEIGHT, cornerToPenaltyBox(w), 0], //7
      [PENALTY_BOX_HEIGHT, w - cornerToPenaltyBox(w), 0], //8
      [0, w - cornerToPenaltyBox(w), 0], //9
      [h, cornerToPenaltyBox(w), 0], //16m top
      [h - PENALTY_BOX_HEIGHT, cornerToPenaltyBox(w), 0], //11
      [h - PENALTY_BOX_HEIGHT, w - cornerToPenaltyBox(w), 0], //12
      [h, w - cornerToPenaltyBox(w), 0], //13
      [0, cornerToGoalBox(w), 0], //14
      [GOAL_BOX_HEIGHT, cornerToGoalBox(w), 0], //15
      [GOAL_BOX_HEIGHT, w - cornerToGoalBox(w), 0], //16
      [0, w - cornerToGoalBox(w), 0], //17
      [h, cornerToGoalBox(w), 0], //18
      [h - GOAL_BOX_HEIGHT, cornerToGoalBox(w), 0], //19
      [h - GOAL_BOX_HEIGHT, w - cornerToGoalBox(w), 0], //20
      [h, w - cornerToGoalBox(w), 0], //21
      [h / 2, w / 2, 0], //Middle
      [0, cornerToGoalPost(w), 0], //23
      [0, w - cornerToGoalPost(w), 0], //24
      [PENALTY_BOX_HEIGHT, (w - HALF_CIRCLE_WIDTH) / 2, 0], //25
      [PENALTY_BOX_HEIGHT, w - (w - HALF_CIRCLE_WIDTH) / 2, 0], //26
      [h, cornerToGoalPost(w), 0], //27
      [h, w - cornerToGoalPost(w), 0], //28
      [h - PENALTY_BOX_HEIGHT, w - (w - HALF_CIRCLE_WIDTH) / 2, 0], //29
      [h - PENALTY_BOX_HEIGHT, (w - HALF_CIRCLE_WIDTH) / 2, 0], //30
      [h / 2, w - (w - CIRCLE_WIDTH) / 2, 0], //31
      [h / 2, (w - CIRCLE_WIDTH) / 2, 0], //32
      [PENALTY_BOX_HEIGHT, w, 0], //33
      [h - PENALTY_BOX_HEIGHT, w, 0], //34
      [h - PENALTY_BOX_HEIGHT, 0, 0], //35
      [PENALTY_BOX_HEIGHT, 0, 0], //36
      [0, cornerToGoalPost(w), GOAL_POST_HEIGHT], //37 crossbar
      [0, w - cornerToGoalPost(w), GOAL_POST_HEIGHT], //38 crossbar
      [h, cornerToGoalPost(w), GOAL_POST_HEIGHT], //39 crossbar
      [h, w - cornerToGoalPost(w), GOAL_POST_HEIGHT], //40 crossbar
    ],
    shapes: [
      [
        0, 6, 14, 23, 24, 17, 9, 1, 33, 2, 34, 3, 13, 21, 28, 27, 18, 10, 4, 35,
        5, 36, 0,
      ],
    ],
    lines: [
      [2, 31],
      [31, 22],
      [22, 32],
      [32, 5],
      [6, 7],
      [7, 25],
      [25, 26],
      [26, 8],
      [8, 9],
      [10, 11],
      [11, 30],
      [30, 29],
      [29, 12],
      [12, 13],
      [14, 15],
      [15, 16],
      [16, 17],
      [18, 19],
      [19, 20],
      [20, 21],
      [36, 7],
      [35, 11],
      [34, 12],
      [33, 8],
      [23, 37],
      [37, 38],
      [38, 24],
      [27, 39],
      [39, 40],
      [40, 28],
    ],
    corners: [0, 3],
    name: "Full pitch extended (" + w + "x" + h + ")",
  };
};

const HOCKEY_RADIUS_BOX = 14.63;
const HOCKEY_GOAL_WIDTH = 3.66;
const HOCKEY_POST_TO_1ST_MARKER = 4.98;
const HOCKEY_POST_TO_2ND_MARKER = 9.98;
const HOCKEY_PENALTY_SPOT = 6.4;
const HOCKEY_WIDTH = 55.0;
const HOCKEY_HEIGHT = 91.4;
const HOCKEY_SEGMENT_WIDTH = 22.85;
const HOCKEY_LONG_CORNER_LINE = 14.63;

const hockeyBoxWidth = HOCKEY_GOAL_WIDTH + 2 * HOCKEY_RADIUS_BOX;
const leftPost = HOCKEY_WIDTH / 2 - HOCKEY_GOAL_WIDTH / 2;
const rightPost = HOCKEY_WIDTH / 2 + HOCKEY_GOAL_WIDTH / 2;

export const HockeyPitchModel = (w: number, h: number): TModel => {
  return {
    worldCoords: [
      [0, 0, 0], //0
      [0, (HOCKEY_WIDTH - hockeyBoxWidth) / 2, 0], //1
      [0, leftPost - HOCKEY_POST_TO_2ND_MARKER, 0], //2
      [0, leftPost - HOCKEY_POST_TO_1ST_MARKER, 0], //3
      [0, leftPost, 0], //4
      [0, rightPost, 0], //5
      [0, rightPost + HOCKEY_POST_TO_1ST_MARKER, 0], //6
      [0, rightPost + HOCKEY_POST_TO_2ND_MARKER, 0], //7
      [0, HOCKEY_WIDTH - (HOCKEY_WIDTH - hockeyBoxWidth) / 2, 0], //8
      [0, HOCKEY_WIDTH, 0], //9
      [HOCKEY_LONG_CORNER_LINE, HOCKEY_WIDTH, 0], //10
      [HOCKEY_SEGMENT_WIDTH, HOCKEY_WIDTH, 0], //11
      [2 * HOCKEY_SEGMENT_WIDTH, HOCKEY_WIDTH, 0], //12
      [3 * HOCKEY_SEGMENT_WIDTH, HOCKEY_WIDTH, 0], //13
      [HOCKEY_HEIGHT - HOCKEY_LONG_CORNER_LINE, HOCKEY_WIDTH, 0], //14
      [HOCKEY_HEIGHT, HOCKEY_WIDTH, 0], //15
      [HOCKEY_HEIGHT, HOCKEY_WIDTH - (HOCKEY_WIDTH - hockeyBoxWidth) / 2, 0], //16
      [HOCKEY_HEIGHT, rightPost + HOCKEY_POST_TO_2ND_MARKER, 0], //17
      [HOCKEY_HEIGHT, rightPost + HOCKEY_POST_TO_1ST_MARKER, 0], //18
      [HOCKEY_HEIGHT, rightPost, 0], //19
      [HOCKEY_HEIGHT, leftPost, 0], //20
      [HOCKEY_HEIGHT, leftPost - HOCKEY_POST_TO_1ST_MARKER, 0], //21
      [HOCKEY_HEIGHT, leftPost - HOCKEY_POST_TO_2ND_MARKER, 0], //22
      [HOCKEY_HEIGHT, (HOCKEY_WIDTH - hockeyBoxWidth) / 2, 0], //23
      [HOCKEY_HEIGHT, 0, 0], //24
      [HOCKEY_HEIGHT - HOCKEY_LONG_CORNER_LINE, 0, 0], //25
      [3 * HOCKEY_SEGMENT_WIDTH, 0, 0], //26
      [2 * HOCKEY_SEGMENT_WIDTH, 0, 0], //27
      [1 * HOCKEY_SEGMENT_WIDTH, 0, 0], //28
      [HOCKEY_LONG_CORNER_LINE, 0, 0], //29
      [HOCKEY_PENALTY_SPOT, HOCKEY_WIDTH / 2, 0], //30
      [HOCKEY_HEIGHT - HOCKEY_PENALTY_SPOT, HOCKEY_WIDTH / 2, 0], //31
    ],
    shapes: [
      [
        0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
        20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 0,
      ],
    ],
    lines: [
      [11, 28],
      [12, 27],
      [13, 26],
    ],
    corners: [0, 15],
    name: "Full pitch extended (" + w + "x" + h + ")",
  };
};
export const models = {
  FutsalModel,
  FutsalModelExtended,
  FutsalModel15,
  FutsalModelTopOfBox,
  FullPitchModel,
  FullPitchExtendedModel,
  HockeyPitchModel,
};
